import type { PlatformChatApi } from '@liks/backend-svc-api/esm/apis/PlatformChatApi';
import type {
  AttachmentType,
  ImageAspectRatio as GeneratedImageAspectRatio,
  ImageFormat,
  PlatformMessage,
  PlatformMessageAttachment,
  PlatformTurn,
  SendPlatformMessageDTO,
} from '@liks/backend-svc-api/esm/models/index';
import type { ImageAspectRatio } from '../types/gallery';
import { hasId } from './platformChatServiceGuards';

export type SendPlatformMessageOptions = {
  webSearchEnabled?: boolean;
  imageGenerationEnabled?: boolean;
  imageFormat?: ImageFormat;
  imageAspectRatio?: ImageAspectRatio;
  attachmentIds?: string[];
  maxResults?: number;
  minScore?: number;
};

export type RegeneratePlatformReplyOptions = {
  maxResults?: number;
  minScore?: number;
};

export function buildSendPlatformMessageDTO(
  content: string,
  options?: SendPlatformMessageOptions,
): SendPlatformMessageDTO {
  return {
    content: content.trim(),
    webSearchEnabled: options?.webSearchEnabled ?? false,
    imageGenerationEnabled: options?.imageGenerationEnabled ?? false,
    ...(options?.attachmentIds && options.attachmentIds.length > 0
      ? { attachmentIds: options.attachmentIds }
      : {}),
    ...(options?.imageGenerationEnabled && options.imageFormat
      ? { imageFormat: options.imageFormat }
      : {}),
    ...(options?.imageGenerationEnabled && options.imageAspectRatio
      ? {
          imageAspectRatio:
            options.imageAspectRatio as GeneratedImageAspectRatio,
        }
      : {}),
  };
}

export async function listMessages(
  platformChatApi: PlatformChatApi,
  sessionId: string,
  options?: { limit?: number; offset?: number },
): Promise<Array<PlatformMessage & { id: string }>> {
  const messages =
    await platformChatApi.deLiksPlatformchatBoundaryPlatformMessageResourceListPlatformChatMessages(
      {
        sessionId,
        limit: options?.limit,
        offset: options?.offset,
      },
    );
  return messages.filter(hasId);
}

export async function sendMessage(
  platformChatApi: PlatformChatApi,
  sessionId: string,
  content: string,
  options?: SendPlatformMessageOptions,
): Promise<PlatformTurn> {
  return platformChatApi.deLiksPlatformchatBoundaryPlatformMessageResourceSendPlatformChatMessage(
    {
      sessionId,
      sendPlatformMessageDTO: buildSendPlatformMessageDTO(content, options),
      ...(options?.maxResults !== undefined
        ? { maxResults: options.maxResults }
        : {}),
      ...(options?.minScore !== undefined
        ? { minScore: options.minScore }
        : {}),
    },
  );
}

export async function regenerateReply(
  platformChatApi: PlatformChatApi,
  sessionId: string,
  messageId: string,
  options?: RegeneratePlatformReplyOptions,
): Promise<(PlatformMessage & { id: string }) | undefined> {
  const regenerated =
    await platformChatApi.deLiksPlatformchatBoundaryPlatformMessageResourceRegeneratePlatformChatReply(
      {
        sessionId,
        id: messageId,
        ...(options?.maxResults !== undefined
          ? { maxResults: options.maxResults }
          : {}),
        ...(options?.minScore !== undefined
          ? { minScore: options.minScore }
          : {}),
      },
    );

  return hasId(regenerated) ? regenerated : undefined;
}

export async function listMessageAttachments(
  platformChatApi: PlatformChatApi,
  sessionId: string,
  messageId: string,
): Promise<Array<PlatformMessageAttachment & { id: string }>> {
  const attachments =
    await platformChatApi.deLiksPlatformchatBoundaryPlatformMessageAttachmentResourceListMessageAttachments(
      { sessionId, messageId, limit: 100 },
    );
  return attachments.filter(hasId);
}

export async function downloadMessageAttachment(
  platformChatApi: PlatformChatApi,
  sessionId: string,
  messageId: string,
  attachmentId: string,
): Promise<Blob> {
  const response =
    await platformChatApi.deLiksPlatformchatBoundaryPlatformMessageAttachmentResourceDownloadMessageAttachmentRaw(
      { sessionId, messageId, attachmentId },
    );
  return response.raw.blob();
}

export async function uploadSessionDraftAttachment(
  platformChatApi: PlatformChatApi,
  sessionId: string,
  file: File,
  attachmentType: AttachmentType = 'DOCUMENT',
): Promise<PlatformMessageAttachment & { id: string }> {
  const uploaded =
    await platformChatApi.deLiksPlatformchatBoundaryPlatformChatSessionAttachmentResourceUploadSessionAttachment(
      {
        sessionId,
        data: file,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
        attachmentType,
        documentName: file.name,
      },
    );

  if (!hasId(uploaded)) {
    throw new Error('Backend returned a message attachment without id');
  }

  return uploaded;
}

export async function transcribeAudio(
  platformChatApi: PlatformChatApi,
  file: File,
): Promise<string> {
  return platformChatApi.deLiksPlatformchatBoundaryPlatformChatTranscriptionResourceTranscribePlatformChatAudio(
    {
      data: file,
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      size: file.size,
    },
  );
}

export async function deleteSessionDraftAttachment(
  platformChatApi: PlatformChatApi,
  sessionId: string,
  attachmentId: string,
): Promise<void> {
  await platformChatApi.deLiksPlatformchatBoundaryPlatformChatSessionAttachmentResourceDeleteSessionDraftAttachment(
    {
      sessionId,
      attachmentId,
    },
  );
}

export async function listSessionDraftAttachments(
  platformChatApi: PlatformChatApi,
  sessionId: string,
  limit = 100,
): Promise<Array<PlatformMessageAttachment & { id: string }>> {
  const attachments =
    await platformChatApi.deLiksPlatformchatBoundaryPlatformChatSessionAttachmentResourceListSessionDraftAttachments(
      { sessionId, limit },
    );
  return attachments.filter(hasId);
}
